/* @flow */
import React, { useState, useEffect, useMemo } from "react";
import { connect, type MapStateToProps } from "react-redux";
import querystring from "querystring";
import Media from "react-media";
import moment from "moment";
import styled from "styled-components";

import Money from "../components/Money";
import Private from "../components/Private";
import Gettext from "../components/common/Gettext";
import Refund from "../components/common/Refund";
import { get } from "../components/common/fetch";
import withData from "../components/withData";
import { withDeposit } from "../components/common/withDeposit";
import HistoryPage from "../components/HistoryPages/HistoryPage";
import Period from "../components/HistoryPages/Period";
import HistorySelect from "../components/HistoryPages/HistorySelect";
import Statistics from "../components/HistoryPages/Statistics";
import HistoryTable from "../components/HistoryPages/HistoryTable";
import FeatureFlags from "../FeatureFlags";
import OperationCheque from "../components/Trade/OperationCheque";
import {
  ModalConsumer,
  type ShowModal
} from "../components/common/ModalProvider";

import type { Profile } from "../redux/CommonTypes";
import type { State as ReduxState } from "../redux/reducer";

import {
  CalendarContainer,
  CalendarInner,
  MobilePeriodInner,
  Button
} from "../components/HistoryPages/shared-styles";
import { tournamentCurrency } from "../components/Icons/Kuchka";

const BidType = styled.span`
  font-weight: 700;
  color: ${({ theme, bidType }) => {
    switch (bidType) {
      case true:
        return theme.upper;
      case false:
        return theme.lower;
      default:
        return theme.ticksFontColor;
    }
  }};
`;

type Props = {
  type?: string,
  showModal: ShowModal,
  showDeposit: boolean,
  profile: Profile,
  tradeMode: "tournament" | "binary"
};

function TradingHistory(props: Props) {
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("empty");
  const [page_size, setPageSize] = useState(15);
  const [pages, setPages] = useState(-1);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(14, "days"));
  const [endDate, setEndDate] = useState(moment());
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [options, setOptions] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [errorText, setErrorText] = useState(null);
  const [isTournament, setIsTournament] = useState(false);

  function updateState() {
    const _data = Array.from(arguments);
    _data.forEach(state => {
      switch (state.key) {
        default:
        case "startDate":
          setStartDate(moment(state.value));
          break;
        case "endDate":
          setEndDate(moment(state.value));
          break;
      }
    });
  }

  const requestBinaryHistory = () => {
    setLoading(true);

    const params = {
      page,
      page_size,
      date_from: startDate.locale("en").format("DD/MM/YYYY"),
      date_till: endDate.locale("en").format("DD/MM/YYYY")
    };

    get(`/api/trading-history?${querystring.stringify(params)}`)
      .then(json => {
        const { data: _data = [], pages: _pages = -1, status } = json;

        if (status === "ERROR") {
          const _errorText = json["0"];
          setErrorText(_errorText);
        } else {
          setErrorText(null);
        }

        setStatus("resolved");
        setLoading(false);
        setData(_data);
        setPages(_pages);
        setIsTournament(false);
      })
      // если у API ошибка, мы делаем catch
      // и убираем лоадер, чтобы не смущал
      .catch(error => {
        setStatus("rejected");
        setLoading(false);
        setData([]);
        setPages(-1);
        setIsTournament(false);
        // а также логируем полученную ошибку
        console.error(error);
      });
  };

  const requestTournamentHistory = () => {
    setLoading(true);

    const params = {
      limit: page_size,
      offset: page_size * page,
      date_from: startDate.unix(),
      date_till: endDate.unix()
    };

    get(`/api/tournaments/trading-history?${querystring.stringify(params)}`)
      .then(json => {
        const { payload = {}, status } = json;

        if (status === "ERROR") {
          const _errorText = json["0"];
          setErrorText(_errorText);
        } else {
          setErrorText(null);
        }

        const { items = [], count = 0 } = payload;
        const _pages = Math.ceil(count / page_size);

        setStatus("resolved");
        setData(items);
        setPages(_pages);
        setLoading(false);
        setIsTournament(true);
      })
      // если у API ошибка, мы делаем catch
      // и убираем лоадер, чтобы не смущал
      .catch(error => {
        setStatus("rejected");
        setData([]);
        setPages(-1);
        setLoading(false);
        setIsTournament(true);
        // а также логируем полученную ошибку
        console.error(error);
      });
  };

  const setAccount = val => {
    const value = val.value;
    const _selectedAccount = options.find(acc => acc.value === value);
    setSelectedAccount(
      Array.isArray(_selectedAccount) ? _selectedAccount[0] : _selectedAccount
    );
  };

  const handleResize = e => {
    setWindowWidth(window.innerWidth);
  };

  useMemo(() => {
    setPage(0);
  }, [startDate, endDate, page_size, selectedAccount]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (document.body) document.body.style.overflow = "";
    };
  }, [windowWidth]);

  useEffect(() => {
    get("/api/getAccounts").then(json => {
      let _selectedAccount;
      let _options = json.accounts.reduce((acc, curr, i) => {
        const label =
          curr.type === "demo" ? (
            <Gettext>Demo account</Gettext>
          ) : (
            <Gettext>Real account</Gettext>
          );
        if (curr.isActive) _selectedAccount = { value: curr.id, label };

        acc.push({ value: curr.id, label });
        return acc;
      }, []);

      if (FeatureFlags.enableTournaments === true) {
        if (props.tradeMode === "tournament") {
          _selectedAccount = {
            value: "tournament",
            label: <Gettext>Tournaments</Gettext>
          };
        }
        setSelectedAccount(_selectedAccount);
        setIsTournament(_selectedAccount === "tournament");
        setOptions([
          ..._options,
          {
            value: "tournament",
            label: <Gettext>Tournaments</Gettext>
          }
        ]);
      } else {
        setSelectedAccount(_selectedAccount);
        setIsTournament(false);
        setOptions([..._options]);
      }
    });
  }, []);

  useEffect(() => {
    if (selectedAccount != null) {
      if (
        FeatureFlags.enableTournaments === true &&
        selectedAccount.value === "tournament"
      ) {
        return requestTournamentHistory();
      }

      get(`/ajax/setAccount?id=${selectedAccount.value}`).then(() =>
        requestBinaryHistory()
      );
    }
  }, [page, page_size, selectedAccount, startDate, endDate]);

  useEffect(() => {
    if (selectedAccount != null && status === "empty") {
      if (
        FeatureFlags.enableTournaments === true &&
        selectedAccount.value === "tournament"
      ) {
        requestTournamentHistory();
      } else {
        requestBinaryHistory();
      }
    }
  }, [status]);

  if (selectedAccount === "") {
    return null;
  }

  const currency = isTournament ? tournamentCurrency : null;

  // колонки, в которые потом рендерятся наши замечательные данные
  const columns = [
    {
      Header: <Gettext>ID</Gettext>,
      accessor: "id",
      show: windowWidth < 960 ? false : true,
      style: { fontFamily: "Tahoma, Roboto, Arial, sans-serif" }
    },
    {
      Header: <Gettext>Period</Gettext>,
      accessor: "op_interval",
      show: windowWidth < 480 ? false : true,
      minWidth: windowWidth < 960 ? 62 : 74,
      Cell: ({ row }) => {
        const { op_interval } = row;

        return (
          <span>
            {op_interval > 59 ? (
              <span>
                {op_interval / 60}
                <Gettext>min</Gettext>
              </span>
            ) : (
              <span>
                {op_interval}
                <Gettext>sec</Gettext>
              </span>
            )}
          </span>
        );
      }
    },
    {
      Header:
        windowWidth < 960 ? (
          <span>
            <Gettext>Opening</Gettext>-<Gettext>Closing</Gettext>
          </span>
        ) : (
          <Gettext>Opening</Gettext>
        ),
      accessor: "open_timestamp",
      minWidth: 114,
      style: { overflow: "visible", flexWrap: "wrap", whiteSpace: "normal" },
      Cell: ({ row }) => {
        let { open_timestamp, close_timestamp } = row;

        // Я убрал проверку на наличие времени закрытия, потому что не заметил
        // чтобы апи отдавал незакрытые сделки
        let openTime = moment(open_timestamp * 1000);
        let closeTime = moment(close_timestamp * 1000);

        if (windowWidth < 960) {
          if (openTime.isSame(closeTime, "day")) {
            return (
              <span>
                {openTime.format("DD/MM/YYYY")}
                <br />
                {openTime.format("HH:mm")} — {closeTime.format("HH:mm")}
              </span>
            );
          } else {
            return (
              <span>
                {openTime.format("DD/MM/YYYY HH:mm")} —{" "}
                {closeTime.format("DD/MM/YYYY HH:mm")}
              </span>
            );
          }
        } else {
          return openTime.format("DD/MM/YYYY HH:mm");
        }
      }
    },
    {
      Header: <Gettext>Closing</Gettext>,
      accessor: "close_timestamp",
      minWidth: 114,
      style: { whiteSpace: "normal" },
      show: windowWidth < 960 ? false : true,
      Cell: ({ row }) => {
        let { close_timestamp } = row;

        let closeTime = moment(close_timestamp * 1000);
        return closeTime.format("DD/MM/YYYY HH:mm");
      }
    },
    {
      Header: <Gettext>Asset</Gettext>,
      accessor: "stock",
      style: { fontWeight: windowWidth < 960 ? 700 : 400, color: "#000" }
    },
    {
      Header: <Gettext>Type</Gettext>,
      accessor: "op_type",
      minWidth: windowWidth < 960 ? 34 : 74,
      Cell: ({ row }) => {
        const BID_TYPE_MOBILE = (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fillRule="evenodd"
            width="16px"
            height="16px"
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            {row.op_type === 1 ? (
              <path d="M15.992,13.993 L15.992,0.001 L1.999,0.001 L1.999,3.998 L9.426,3.998 L0.618,12.807 L3.186,15.376 L11.995,6.567 L11.995,13.993 L15.992,13.993 Z" />
            ) : (
              <path d="M15.992,1.990 L15.992,15.982 L1.999,15.982 L1.999,11.985 L9.426,11.985 L0.618,3.176 L3.186,0.607 L11.995,9.416 L11.995,1.990 L15.992,1.990 Z" />
            )}
          </svg>
        );

        const BID_TYPE_DESKTOP =
          row.op_type === 1 ? (
            <Gettext>Upper</Gettext>
          ) : (
            <Gettext>lower</Gettext>
          );

        return (
          <BidType bidType={row.op_type === 1}>
            {windowWidth < 960 ? BID_TYPE_MOBILE : BID_TYPE_DESKTOP}
          </BidType>
        );
      }
    },
    {
      Header: <Gettext>Difference</Gettext>,
      accessor: "diff",
      show: windowWidth < 960 ? false : true
    },
    {
      Header: <Gettext>Amount</Gettext>,
      accessor: "amount",
      show: windowWidth < 480 ? false : true,
      style: {
        fontWeight: "bold",
        color: "#000"
      },
      Cell: ({ row }) => <Money currency={currency}>{row.amount}</Money>
    },
    {
      Header: <Gettext>Result</Gettext>,
      accessor: "profit",
      Cell: ({ row }) => {
        const { profit } = row;

        let bidType = Math.sign(profit) !== -1;
        if (profit === 0) {
          bidType = null;
        }

        return (
          <BidType bidType={bidType}>
            {profit !== 0 ? (
              <Money currency={currency}>{profit}</Money>
            ) : (
              <Refund />
            )}
          </BidType>
        );
      }
    },
    {
      Header: <Gettext>Balance</Gettext>,
      accessor: "balance",
      show: windowWidth < 480 ? false : true,
      style: { color: "#4e2a81" },
      Cell: ({ row }) => <Money currency={currency}>{row.balance}</Money>
    }
  ];

  return (
    <HistoryPage type={props.type} title={<Gettext>Trading history</Gettext>}>
      <CalendarContainer>
        <CalendarInner>
          <Media
            query="(min-width: 959px)"
            render={() => (
              /* DESKTOP */
              <Period
                windowWidth={windowWidth}
                startDate={startDate}
                endDate={endDate}
                updateState={updateState}
              />
            )}
          />
          {props.showDeposit === true && (
            <HistorySelect
              // async
              name="account-select"
              value={selectedAccount}
              options={options}
              onChange={setAccount}
              disabled={!props.showDeposit}
            >
              <Gettext>Account</Gettext>:
            </HistorySelect>
          )}
          <Media query="(min-width: 959px)">
            {matches =>
              matches ? (
                <Button
                  onClick={() => {
                    setPage(0);
                    setStatus("empty");
                  }}
                >
                  <Gettext>Show</Gettext>
                </Button>
              ) : (
                /* MOBILE */
                props.showDeposit === true && <Statistics />
              )
            }
          </Media>
        </CalendarInner>
      </CalendarContainer>
      <Media query="(min-width: 959px)">
        {matches =>
          matches ? (
            /* DESKTOP */
            props.showDeposit === true && <Statistics />
          ) : (
            /* MOBILE */
            <MobilePeriodInner>
              <Period
                windowWidth={windowWidth}
                startDate={startDate}
                endDate={endDate}
                updateState={updateState}
              />
              <Button
                onClick={() => {
                  setPage(0);
                  setStatus("empty");
                }}
              >
                <Gettext>Show</Gettext>
              </Button>
            </MobilePeriodInner>
          )
        }
      </Media>
      <HistoryTable
        type={props.type}
        columns={columns}
        column={{ minWidth: windowWidth < 960 ? 62 : 94 }}
        loading={loading}
        pages={pages}
        data={data}
        manual={true}
        startDate={startDate}
        endDate={endDate}
        errorText={errorText}
        page={page}
        pageSize={page_size}
        onPageChange={({ selected }: { selected: number }) => {
          setPage(selected);
        }}
        onPageSizeChange={(pageSize: number) => {
          setPageSize(pageSize);
        }}
        onClick={(e, handleOriginal) => {
          props.showModal(OperationCheque, {
            operation: e,
            type:
              selectedAccount.value === "tournament" ? "tournament" : "binary"
          });

          if (handleOriginal) {
            handleOriginal();
          }
        }}
      />
    </HistoryPage>
  );
}

let _TradingHistory = props => (
  <ModalConsumer>
    {modal => <TradingHistory {...props} showModal={modal.showModal} />}
  </ModalConsumer>
);

const mapStateToProps: MapStateToProps<*, *, *> = (state: ReduxState) => ({
  tradeMode: state.settings.mode
});

export default Private(
  withData(["profile"])(withDeposit(connect(mapStateToProps)(_TradingHistory)))
);



// WEBPACK FOOTER //
// ./src/pages/trading-history.js